import type { ReferenceDataSource } from "@/types/reference-data";

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Translate one catalog label through its i18n key, falling back to the raw
 *  column value.
 *
 *  `mygrow.reference_data_sources` stores display_name/description/origin in a
 *  single language (the seed writes pt-BR), so rendering the column directly
 *  leaves those three fields untranslated on every screen. The seed also fills
 *  `<column>_key` (e.g. `ref_grow_skus.display_name_key`), which the locale
 *  bundles carry for every source — this resolves through it and keeps the
 *  column as the fallback for a source that has no key yet (a row seeded before
 *  the key columns existed, or one added after the bundles were written).
 */
export function catalogLabel(
  t: Translate,
  key: string | null | undefined,
  fallback: string | null | undefined,
): string {
  const raw = fallback ?? "";
  if (!key) return raw;
  // `defaultValue` keeps an unknown key from rendering as the key itself.
  return t(key, { defaultValue: raw }) || raw;
}

/** The three translated labels of one source, for the inventory row and hero. */
export function sourceLabels(t: Translate, source: ReferenceDataSource | undefined) {
  return {
    displayName: catalogLabel(t, source?.display_name_key, source?.display_name),
    description: catalogLabel(t, source?.description_key, source?.description),
    origin: catalogLabel(t, source?.origin_key, source?.origin),
  };
}

/** `operation_type` of an ingestion run → translated pill label. An unknown
 *  operation (a newer API) falls through to its raw value instead of blank. */
export function operationLabel(t: Translate, operation: string): string {
  const labels: Record<string, string> = {
    upload: t("upload"),
    ingest: t("ingest"),
    edit: t("edit"),
    promote: t("promote"),
    rollback: t("rollback"),
    discard: t("discard"),
  };
  return labels[operation] ?? operation;
}

/** `status` of an ingestion run → translated audit-row label. */
export function runStatusLabel(t: Translate, status: string): string {
  const labels: Record<string, string> = {
    success: t("success"),
    failed: t("failed"),
    partial: t("partial"),
    running: t("running"),
    queued: t("queued"),
  };
  return labels[status] ?? status;
}
